<template>
  <tab-list
      v-model="currentTab"
      :tabs-enum="tabs"
      :title="title"
      :loading="isLoading || !isUserLoaded"
      :default-tab="defaultTab"
      @change="changeTab">
    <template v-slot:tabItem>
      <v-progress-linear :active="isLoading && showTradeProcess" color="loader" indeterminate />
      <lot-general-info
          v-if="showGeneralInfo"
          :lot="lot"
          :loading="isLoading"
          :positions="positions"
          :sessions="sessions"
          @change="initialize" />
      <lot-documentation
          v-if="showLotDocuments"
          :lot="lot" />
      <trade-process-documentation
          v-if="showTradeProcessDocumentation"
          :lot="lot" />
      <trade-orders
          v-if="showTradeOrders"
          :lot="lot"
          :can-get-offer-registry="lastSessionFinished" />
      <trade-results
          v-if="showTradeResults"
          :lot="lot" />
      <lot-requests
          v-if="showLotRequests"
          :lot="lot" />
      <bidding-view
          v-if="showTradeProcess"
          :lot="lot"
          :positions="positions"
          @finished="reload" />
    </template>
  </tab-list>
</template>

<script>
  import Vue from 'vue'
  import LotGeneralInfo from './LotGeneralInfo.vue'
  import LotDocumentation from './LotDocumentation.vue'
  import TradeProcessDocumentation from './TradeProcessDocumentation.vue'
  import TradeOrders from './TradeOrders.vue'
  import LotRequests from './LotRequests.vue'
  import TradeResults from './TradeResults.vue'
  import BiddingView from './BiddingView.vue'
  import TabList from '@/components/reused/ui/TabList.vue'
  import lotMixin from '@/utils/mixins/scoped/lotMixin'
  import { LOT_TABS_ENUM } from '@/constants/enums'
  import { LOT_TABS, SESSION_LOCK_STATUSES } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import {
    LOT_MANAGEMENT, LOT_POSITIONS_MANAGEMENT,
    LOT_SESSIONS_MANAGEMENT, CURRENT_USER
  } from '@/constants/store.js'

  export default {
    components: {
      TabList,
      LotGeneralInfo,
      LotDocumentation,
      TradeProcessDocumentation,
      TradeOrders,
      TradeResults,
      BiddingView,
      LotRequests
    },
    mixins: [lotMixin],
    props: {
      lotId: {
        type: String,
        required: true
      },
      lotType: {
        type: String,
        required: true
      },
      tab: {
        type: String
      }
    },
    data () {
      return {
        LOT_TABS_ENUM,
        isLoading: false,
        currentTab: this.tab || LOT_TABS.GENERAL_INFO,
        defaultTab: LOT_TABS.GENERAL_INFO
      }
    },
    computed: {
      ...mapState(LOT_MANAGEMENT.NAMESPACE, {
        currentLot: state => (state[LOT_MANAGEMENT.STATE.CURRENT_LOT])
      }),
      ...mapState(CURRENT_USER.NAMESPACE, {
        contactPerson: state => (state[CURRENT_USER.STATE.CONTACT_PERSON] || null)
      }),
      ...mapState(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        positions: state => (state[LOT_POSITIONS_MANAGEMENT.STATE.POSITIONS] || [])
      }),
      title () {
        return this.$t('Просмотр торговой процедуры № {0}', [this.lot.lotNumber || ''])
      },
      tabs () {
        let tabs = { ...LOT_TABS_ENUM }
        if (!this.canViewTradeProcess) {
          tabs = Vue.helpers.excludeObjectProps(tabs, [LOT_TABS.TRADE_PROCESS])
        }
        if (!this.canViewTradeOrders) {
          tabs = Vue.helpers.excludeObjectProps(tabs, [LOT_TABS.ORDERS])
        }
        if (!this.canViewTradeResults) {
          tabs = Vue.helpers.excludeObjectProps(tabs, [LOT_TABS.TRADE_RESULTS])
        }
        if (!this.isAdmin && !this.isLotOwner(this.lot)) {
          tabs = Vue.helpers.excludeObjectProps(tabs, [LOT_TABS.LOT_REQUESTS])
        }
        return tabs
      },
      showGeneralInfo () {
        return this.currentTab === LOT_TABS.GENERAL_INFO
      },
      showLotRequests () {
        return this.currentTab === LOT_TABS.LOT_REQUESTS &&
          (this.isAdmin || this.isLotOwner(this.lot))
      },
      showLotDocuments () {
        return this.currentTab === LOT_TABS.LOT_DOCUMENTS
      },
      showTradeOrders () {
        return this.currentTab === LOT_TABS.ORDERS &&
          this.canViewTradeOrders
      },
      canViewTradeOrders () {
        if (!this.isAdmin && !this.isLotOwner(this.lot)) {
          return false
        }
        if (!this.firstSessionStart) {
          return false
        }
        if (!this.lastSessionFinished) {
          return false
        }
        if (!this.lastSessionFinished && this.isLotOwner(this.lot)) {
          return false
        }
        return true
      },
      showTradeResults () {
        return this.currentTab === LOT_TABS.TRADE_RESULTS &&
          this.canViewTradeResults
      },
      canViewTradeResults () {
        return (this.isAdmin || this.isLotOwner(this.lot)) &&
          this.lastSessionFinished
      },
      showTradeProcess () {
        return this.currentTab === LOT_TABS.TRADE_PROCESS &&
          this.canViewTradeProcess
      },
      canViewTradeProcess () {
        return (this.isAdmin || this.isLotOwner(this.lot)) &&
          this.sessionInProcess
      },
      showTradeProcessDocumentation () {
        return this.currentTab === LOT_TABS.TRADE_PROCESS_DOCUMENTATION
      },
      lot () {
        return this.currentLot || {
          lotId: this.lotId,
          lotType: this.lotType
        }
      },
      lastSessionFinished () {
        const session = this.lastOrCurrentSession
        if (!session || !session.dateEndWithProlongation) {
          return false
        }
        if (session.lockStatus &&
          session.lockStatus !== SESSION_LOCK_STATUSES.UNLOCKED) {
          return true
        }
        const now = this.$moment()// .tz('Europe/Moscow')
        const dateEnd = this.$moment(session.dateEndWithProlongation)
        return now.isAfter(dateEnd)
      },
      sessionInProcess () {
        const session = this.lastOrCurrentSession
        if (!session ||
          !session.dateStart ||
          !session.dateEndWithProlongation) {
          return false
        }
        if (session.lockStatus &&
          session.lockStatus !== SESSION_LOCK_STATUSES.UNLOCKED) {
          return false
        }
        const now = this.$moment()
        const dateStart = this.$moment(session.dateStart)
        const dateEnd = this.$moment(session.dateEndWithProlongation)
        return now.isBetween(dateStart, dateEnd)
      },
      firstSession () {
        if (!this.sessions || this.sessions.length === 0) {
          return null
        }
        return this.sessions[0]
      },
      firstSessionStart () {
        const session = this.firstSession
        if (!session || !session.dateStart) {
          return false
        }
        const now = this.$moment()// .tz('Europe/Moscow')
        const dateStart = this.$moment(session.dateStart)
        return now.isAfter(dateStart)
      },
      isUserLoaded () {
        return !!this.contactPerson
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (!vm.lotId) {
          return
        }
        vm.initialize()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        getLotInfoAction: LOT_MANAGEMENT.ACTIONS.GET_LOT_INFO,
        clearLotInfoAction: LOT_MANAGEMENT.ACTIONS.CLEAR_LOT_INFO
      }),
      ...mapActions(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        getPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.GET_POSITIONS_LIST,
        clearPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.CLEAR_POSITIONS_LIST
      }),

      ...mapActions(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        getSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.GET_SESSIONS_LIST,
        clearSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.CLEAR_SESSIONS_LIST
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotInfoAction(),
            this.clearPositionsListAction(),
            this.clearSessionsListAction()
          ])
      },
      initialize: async function () {
        const model = {
          lotId: this.lotId,
          lotType: this.lotType,
          premiumType: this.lot.premiumType
        }
        try {
          this.isLoading = true
          await Promise.all([
            this.getLotInfoAction(model),
            this.getPositionsListAction(model),
            this.getSessionsListAction(model)
          ])
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      reload: function () {
        this.replaceCurrentRoute(this.getRoute(this.defaultTab))
          .then(() => {
            window.location.reload()
          })
      },
      changeTab: function (tabValue) {
        this.replaceCurrentRoute(this.getRoute(tabValue))
          .then(() => {
            // обновляем данные при переходе на основную вкладку,
            // чтобы получить актуальные флаги редактирования
            if (tabValue === LOT_TABS.GENERAL_INFO) {
              this.initialize()
            }
          })
      },
      getRoute: function (tabValue) {
        return `${this.urlRootPath}/lot/${this.lotType}/${this.lotId}/${tabValue}`
      }
    }
  }
</script>
